import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { X, Shield, Award, CheckCircle, TrendingUp, Phone, Mail, Clock, Star, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';

const ConsultationPopup = ({ children, className }) => {
  const { t, language, direction } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    amount: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit');
      }
      
      toast.success(
        direction === 'rtl' 
          ? 'تم استلام طلبك! سنتواصل معك خلال 24 ساعة' 
          : 'Request received! We will contact you within 24 hours'
      );
      setFormData({ name: '', phone: '', email: '', amount: '' });
      setIsOpen(false);
    } catch (error) {
      toast.error(
        direction === 'rtl'
          ? 'حدث خطأ، يرجى المحاولة مرة أخرى'
          : 'An error occurred, please try again'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const isRTL = direction === 'rtl';

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent 
        className="max-w-2xl p-0 overflow-hidden bg-transparent border-0 shadow-none sm:max-h-[95vh] max-h-[100vh] data-[state=open]:animate-none data-[state=closed]:animate-none" 
        aria-describedby="consultation-desc"
        style={{ animationDuration: '0.1s' }}
      >
        <DialogTitle className="sr-only">
          {isRTL ? 'احصل على استشارة مجانية' : 'Get Free Consultation'}
        </DialogTitle>
        <DialogDescription id="consultation-desc" className="sr-only">
          Free consultation form for fund recovery services
        </DialogDescription>
        
        {/* Main Container */}
        <div className="relative sm:max-h-[90vh] max-h-[95vh] overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Gold Border - No animation for better performance */}
          <div className="absolute -inset-1 bg-gradient-to-r from-[#C9A55A] via-[#D4AF37] to-[#C9A55A] rounded-3xl opacity-75 blur-sm" />
          
          {/* Content */}
          <div className="relative bg-gradient-to-br from-[#0A1628] via-[#0f2444] to-[#0A1628] rounded-3xl overflow-hidden">
            
            {/* Close Button - Instant response */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(false);
              }}
              className="absolute top-4 right-4 z-50 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center border border-white/20 cursor-pointer active:scale-95"
              style={{ transition: 'none' }}
              data-testid="popup-close-btn"
              type="button"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            
            {/* Header Section */}
            <div className="relative px-8 pt-8 pb-6 text-center border-b border-[#C9A55A]/20">
              {/* Decorative Pattern */}
              <div className="absolute inset-0 opacity-5" style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, #C9A55A 1px, transparent 0)`,
                backgroundSize: '20px 20px'
              }} />
              
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/40 rounded-full mb-4">
                <Sparkles className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-bold">
                  {t.ui.freeConsultation}
                </span>
              </div>
              
              {/* Title */}
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                {t.ui.recoverFundsNow}
              </h2>
              <p className="text-gray-400 text-base">
                {t.ui.startRecoveryJourney}
              </p>
            </div>

            {/* Trust Badges */}
            <div className="px-6 py-3 bg-[#C9A55A]/5 border-b border-[#C9A55A]/20">
              <div className={`flex flex-wrap justify-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {/* SRA Licensed */}
                <div className={`flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Shield className="w-4 h-4 text-[#C9A55A]" />
                  <span className="text-white text-xs font-semibold">
                    {t.ui.sraLicensed}
                  </span>
                </div>
                
                {/* Success Rate */}
                <div className={`flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  <span className="text-white text-xs font-semibold">
                    {t.ui.successRate}
                  </span>
                </div>
                
                {/* No Win No Fee */}
                <div className={`flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Award className="w-4 h-4 text-blue-400" />
                  <span className="text-white text-xs font-semibold">
                    {t.ui.noWinNoFee}
                  </span>
                </div>
              </div>
            </div>

            {/* Form Section */}
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Input */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-300 mb-1.5 ${isRTL ? 'text-right' : ''}`}>
                    {t.ui.fullName}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={isRTL ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                    className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#C9A55A]/50 focus:ring-2 focus:ring-[#C9A55A]/20 transition-all ${isRTL ? 'text-right' : ''}`}
                    data-testid="popup-name"
                  />
                </div>
                
                {/* Phone & Email Row */}
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-sm font-semibold text-gray-300 mb-1.5 ${isRTL ? 'text-right' : ''}`}>
                      {t.ui.phoneNumber}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder={isRTL ? '+971 XX XXX XXXX' : '+1 XXX XXX XXXX'}
                      className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#C9A55A]/50 focus:ring-2 focus:ring-[#C9A55A]/20 transition-all ${isRTL ? 'text-right' : ''}`}
                      data-testid="popup-phone"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-semibold text-gray-300 mb-1.5 ${isRTL ? 'text-right' : ''}`}>
                      {t.ui.emailAddress}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder={isRTL ? 'example@email.com' : 'example@email.com'}
                      className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#C9A55A]/50 focus:ring-2 focus:ring-[#C9A55A]/20 transition-all ${isRTL ? 'text-right' : ''}`}
                      data-testid="popup-email"
                    />
                  </div>
                </div>
                
                {/* Amount Input */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-300 mb-1.5 ${isRTL ? 'text-right' : ''}`}>
                    {t.ui.amountLost}
                  </label>
                  <input
                    type="text"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    placeholder={isRTL ? 'مثال: $50,000' : 'e.g., $50,000'}
                    className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#C9A55A]/50 focus:ring-2 focus:ring-[#C9A55A]/20 transition-all ${isRTL ? 'text-right' : ''}`}
                    data-testid="popup-amount"
                  />
                </div>
                
                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 bg-gradient-to-r from-[#C9A55A] to-[#D4AF37] text-[#0A1628] rounded-xl font-bold text-base shadow-[0_0_30px_rgba(201,165,90,0.4)] hover:shadow-[0_0_50px_rgba(201,165,90,0.6)] transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                  data-testid="popup-submit"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-[#0A1628] border-t-transparent rounded-full animate-spin" />
                      {t.ui.sending}
                    </>
                  ) : (
                    <>
                      <Phone className="w-5 h-5" />
                      {t.ui.getConsultationNow}
                    </>
                  )}
                </button>
                
                {/* Back/Cancel Button */}
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="w-full py-3 bg-transparent border border-white/20 text-gray-400 hover:text-white hover:border-white/40 rounded-xl font-medium text-sm transition-all"
                  data-testid="popup-back-btn"
                >
                  {t.ui.backToWebsite} ←
                </button>
              </form>
              
              {/* Bottom Info */}
              <div className={`mt-4 flex flex-wrap items-center justify-center gap-4 text-xs text-gray-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Clock className="w-3.5 h-3.5 text-[#C9A55A]" />
                  <span>{t.ui.responseWithin24h}</span>
                </div>
                <div className={`flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{t.ui.confidential}</span>
                </div>
              </div>
            </div>

            {/* Stats Bar */}
            <div className="px-6 py-4 bg-[#C9A55A]/10 border-t border-[#C9A55A]/20 mb-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xl font-bold text-[#C9A55A]">$15M+</p>
                  <p className="text-xs text-gray-400">{t.ui.recovered}</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-[#C9A55A]">250+</p>
                  <p className="text-xs text-gray-400">{t.ui.casesWon}</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-[#C9A55A]">15+</p>
                  <p className="text-xs text-gray-400">{t.ui.yearsExperience}</p>
                </div>
              </div>
            </div>
            
            {/* Safe area padding for mobile */}
            <div className="h-4 sm:h-0" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConsultationPopup;
